import * as THREE from 'three';
import type { System } from '../../systems';
import type { EntityRegistry } from '../../entities';
import type { InputManager } from '../../input';
import type { AudioManager } from '../../audio';
import type { PlayerShip } from '../ships';
import type { ShipUpgrades } from '../upgrades';
import { Asteroid } from '../sector';
import { MiningBeam } from '../npcs';
import type { Explosions } from '../combat';
import type { Inventory } from '../inventory';
import { rollAsteroidStandardDrop } from '../sector/AsteroidLoot';

export interface ShipMiningBeamHooks {
  /** Fired when an asteroid is destroyed by this beam. Same shape as the
   *  combat hook so the scene can route loot through one place. */
  readonly onAsteroidDestroyed?: (
    asteroid: Asteroid,
    worldPos: THREE.Vector3
  ) => void;
}

export interface ShipMiningBeamOptions {
  /** Damage per second dealt to the targeted asteroid. Tuned so a typical
   *  rock breaks in roughly the same time as the legacy kinetic-gun pace
   *  felt — making the beam the de-facto mining tool. */
  readonly damagePerSecond: number;
  /** Maximum reach. Past this the beam shows but does no damage. */
  readonly range: number;
}

export const DEFAULT_SHIP_MINING_BEAM: ShipMiningBeamOptions = {
  damagePerSecond: 12,
  range: 320
};

const MOUSE_RIGHT = 2;
const MUZZLE_OFFSET = new THREE.Vector3(0, -0.1, -1.4);

/** Right-click ship mining laser. Reuses the NPC `MiningBeam` visual so
 *  player and NPC mining read identically. The system is gated on either
 *  the miner-hauler ship variant (auto-unlocked) or the explicit
 *  ship-mining-beam upgrade purchased at engineering. */
export class ShipMiningBeam implements System {
  readonly id = 'ship-mining-beam';

  private readonly beam: MiningBeam;
  private readonly muzzleWorld = new THREE.Vector3();
  private readonly cameraForward = new THREE.Vector3();
  private readonly aimPoint = new THREE.Vector3();
  private readonly rayDir = new THREE.Vector3();
  private readonly toAsteroid = new THREE.Vector3();
  private readonly hitPoint = new THREE.Vector3();
  private enabled = true;
  private active = false;
  /** Audio cue cooldown so the mining tick doesn't fire every frame. */
  private tickAccumulator = 0;

  constructor(
    private readonly ship: PlayerShip,
    private readonly input: InputManager,
    private readonly entities: EntityRegistry,
    parent: THREE.Object3D,
    private readonly camera: THREE.Camera,
    private readonly explosions: Explosions,
    private readonly audio: AudioManager | null,
    private readonly shipUpgrades: ShipUpgrades,
    private readonly options: ShipMiningBeamOptions = DEFAULT_SHIP_MINING_BEAM,
    private readonly hooks: ShipMiningBeamHooks = {}
  ) {
    this.beam = new MiningBeam(camera);
    parent.add(this.beam.object3D);
  }

  setEnabled(value: boolean): void {
    this.enabled = value;
    if (!value) this.deactivate();
  }

  /** Whether the beam is currently visible / damaging. Useful for HUD. */
  isFiring(): boolean {
    return this.active;
  }

  /** True when the player has access to the beam — either the miner-hauler
   *  ship has it built-in, or the upgrade was purchased. */
  isUnlocked(): boolean {
    if (this.shipUpgrades.has('ship-mining-beam')) return true;
    // Miner-hauler ships ship with the beam mounted by default.
    return this.ship.selection.factionId === 'miners';
  }

  update(dt: number): void {
    if (
      !this.enabled ||
      !this.input.isPointerLocked() ||
      !this.input.isMouseDown(MOUSE_RIGHT) ||
      !this.isUnlocked()
    ) {
      this.deactivate();
      return;
    }

    this.ship.object3D.updateMatrixWorld();
    this.muzzleWorld
      .copy(MUZZLE_OFFSET)
      .applyMatrix4(this.ship.object3D.matrixWorld);

    this.camera.updateMatrixWorld();
    this.cameraForward.set(0, 0, -1).applyQuaternion(this.camera.quaternion);
    this.aimPoint
      .copy(this.camera.position)
      .addScaledVector(this.cameraForward, this.options.range);

    this.rayDir.copy(this.aimPoint).sub(this.muzzleWorld);
    const rayLength = this.rayDir.length();
    if (rayLength < 1e-3) {
      this.deactivate();
      return;
    }
    this.rayDir.multiplyScalar(1 / rayLength);

    const target = this.findClosestAsteroid(this.muzzleWorld, this.rayDir);
    if (target) {
      this.beam.setEndpoints(this.muzzleWorld, this.hitPoint);
      this.beam.tick(dt);
      this.active = true;

      const damage = this.options.damagePerSecond * dt;
      const destroyed = target.takeDamage(damage);
      this.tickAudio(dt);

      if (destroyed) {
        const pos = target.object3D.position;
        this.explosions.spawnDestruction(pos, target.collisionRadius, target.baseTint);
        target.markForRemoval();
        this.audio?.playExplosion(target.collisionRadius);
        this.hooks.onAsteroidDestroyed?.(target, pos);
      } else {
        // Sparks at the contact point for feedback while drilling.
        if (Math.random() < 0.45) {
          this.explosions.spawnHit(this.hitPoint);
        }
      }
    } else {
      // No target — beam still draws to max range so the player sees
      // they're firing, just not hitting anything.
      this.aimPoint.copy(this.muzzleWorld).addScaledVector(this.rayDir, this.options.range);
      this.beam.setEndpoints(this.muzzleWorld, this.aimPoint);
      this.beam.tick(dt);
      this.active = true;
    }
  }

  dispose(): void {
    this.beam.dispose();
    if (this.beam.object3D.parent) {
      this.beam.object3D.parent.remove(this.beam.object3D);
    }
  }

  private deactivate(): void {
    if (!this.active) return;
    this.beam.hide();
    this.active = false;
    this.tickAccumulator = 0;
  }

  /** Iterate live asteroids, find the one whose bounding sphere first
   *  intersects the ray. Reuses the player ship's existing sphere-check
   *  pattern so we don't pull in a Raycaster for one query per frame. */
  private findClosestAsteroid(
    origin: THREE.Vector3,
    dir: THREE.Vector3
  ): Asteroid | null {
    let best: Asteroid | null = null;
    let bestT = this.options.range;

    for (const entity of this.entities.all().values()) {
      if (!(entity instanceof Asteroid) || !entity.isAlive) continue;
      this.toAsteroid.copy(entity.object3D.position).sub(origin);
      const projection = this.toAsteroid.dot(dir);
      if (projection < 0 || projection > this.options.range + entity.collisionRadius) {
        continue;
      }
      const perpSq =
        this.toAsteroid.lengthSq() - projection * projection;
      const radiusSq = entity.collisionRadius * entity.collisionRadius;
      if (perpSq > radiusSq) continue;

      const sphereT = projection - Math.sqrt(Math.max(radiusSq - perpSq, 0));
      const enterT = Math.max(sphereT, 0);
      if (enterT < bestT) {
        bestT = enterT;
        best = entity;
      }
    }

    if (best) {
      this.hitPoint.copy(origin).addScaledVector(dir, bestT);
    }
    return best;
  }

  private tickAudio(dt: number): void {
    if (!this.audio) return;
    this.tickAccumulator += dt;
    if (this.tickAccumulator >= 0.32) {
      this.audio.playMiningTick();
      this.tickAccumulator = 0;
    }
  }
}

/** Re-export the loot helper alongside the system so MainScene only has to
 *  know about one mining module. */
export { rollAsteroidStandardDrop };
